import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  UserKeys,
  getIndexedSharedDrivesForAutocomplete,
} from '../api/users-api';
import { useMemo, useState } from 'react';
import useDebounce from '../utils/useDebounce';
import { SharedDriveModel } from '../../types';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';

interface SharedDrivesBigQueryAutocompleteProps {
  autocompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  newKey?: string;
  errors?: boolean;
  helperText?: string;
  required?: boolean;
  onDriveSelect?: (drive: SharedDriveModel | null) => void;
}

export default function SharedDrivesBigQueryAutocomplete({
  size,
  disabled,
  autocompleteValue,
  setAutoCompleteValue,
  newKey,
  errors,
  helperText,
  required,
  onDriveSelect,
}: SharedDrivesBigQueryAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 500);

  const enabled = debouncedInput !== '' && autocompleteValue === '';

  const { data: sharedDriveData, isFetching: sharedDrivesLoading } = useQuery({
    queryKey: [UserKeys.SharedDrivesAutocomplete, { debouncedInput }],
    queryFn: () =>
      getIndexedSharedDrivesForAutocomplete(
        {
          results: [],
          pageSize: 5,
          currentPage: 0,
          totalCount: 0,
        },
        debouncedInput
      ),
    enabled: enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const options: SharedDriveModel[] = sharedDriveData?.results || [];

  function createNoOptionsText() {
    if (debouncedInput === '') {
      return 'Start typing to search...';
    }
    return 'No shared drives found.';
  }

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  return (
    <Autocomplete
      id="shared-drive-bigquery-autocomplete"
      key={key}
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      size={size}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 350,
          },
        },
      }}
      autoComplete
      autoHighlight
      includeInputInList
      filterSelectedOptions
      noOptionsText={createNoOptionsText()}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option;
        }
        return option.name;
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string') {
          return option === value;
        } else {
          return option.id === value?.id;
        }
      }}
      onChange={(_event, newValue) => {
        if (typeof newValue === 'string') {
          setAutoCompleteValue(newValue);
          onDriveSelect?.(null);
        } else {
          setAutoCompleteValue(newValue?.id || '');
          onDriveSelect?.(newValue || null);
        }
      }}
      renderOption={(props, option: SharedDriveModel) => {
        return (
          <ListItem {...props} key={option.id} dense>
            <ListItemAvatar>
              <Avatar
                src={option.backgroundImageLink}
                sx={{ width: 40, height: 40 }}
              />
            </ListItemAvatar>
            <ListItemText
              id={option.id}
              primary={option.name}
              secondary={`Drive ID: ${option.id || ''}`}
              primaryTypographyProps={{ whiteSpace: 'normal' }}
            />
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={'Search for a shared drive'}
          required={required}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {sharedDrivesLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errors}
          helperText={helperText}
        />
      )}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
    />
  );
}
